import React, {useCallback, useEffect, useMemo, useState} from "react";
import {mergePasswords} from "./crypt";
import {Button, Modal, Select, Spin, Switch, message} from 'antd';
import {YandexDiskApi, resolveRemoteVault} from './yandex-api';
import {ensureIdentityForSalt, getActiveVault} from './account';

const YANDEX_APP_ID = 'f09cbf96ed6349fe8d6fd90a09dc925f';
const REDIRECT_URI = (process.env.NODE_ENV !== 'production') ? 'http://localhost:3000' : 'https://selfpass.ru/';
const DEFAULT_SYNC_INTERVAL_MS = Number(process.env.REACT_APP_YANDEX_SYNC_INTERVAL_MS) || 300000;
const MIN_SYNC_INTERVAL_MS = 10000;
const TOKEN_STORAGE_KEY = 'yandex_token';
const SYNC_ENABLED_KEY = 'yandex_sync';
const SYNC_INTERVAL_KEY = 'yandex_sync_interval_ms';
const LAST_SYNC_KEY = 'yandex_last_sync';
export const YANDEX_OAUTH_RETURN_KEY = 'yandex_oauth_return';

const SYNC_INTERVAL_OPTIONS = [
    {value: 10000, label: '10 секунд'},
    {value: 20000, label: '20 секунд'},
    {value: 30000, label: '30 секунд'},
    {value: 60000, label: '1 минута'},
    {value: 300000, label: '5 минут'},
    {value: 900000, label: '15 минут'},
];

const formatIntervalLabel = (ms) => {
    if (ms < 60000) return `${ms / 1000} секунд`;
    const minutes = ms / 60000;
    if (minutes === 1) return '1 минута';
    if (minutes < 5) return `${minutes} минуты`;
    return `${minutes} минут`;
};

const readSyncIntervalMs = () => {
    const stored = Number(localStorage.getItem(SYNC_INTERVAL_KEY));
    if (Number.isFinite(stored) && stored >= MIN_SYNC_INTERVAL_MS) {
        return stored;
    }
    return DEFAULT_SYNC_INTERVAL_MS;
};

const persistSyncIntervalMs = (ms) => {
    localStorage.setItem(SYNC_INTERVAL_KEY, String(ms));
};

const readLastSync = () => {
    const stored = localStorage.getItem(LAST_SYNC_KEY);
    if (!stored) return null;
    const date = new Date(stored);
    return Number.isNaN(date.getTime()) ? null : date;
};

const persistLastSync = (date) => {
    localStorage.setItem(LAST_SYNC_KEY, date.toISOString());
};

const formatLastSync = (date) =>
    date ? date.toLocaleString() : 'ещё не было';

const readYandexToken = () =>
    sessionStorage.getItem(TOKEN_STORAGE_KEY) || localStorage.getItem(TOKEN_STORAGE_KEY);

const persistYandexToken = (accessToken) => {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
};

export const clearYandexSyncStorage = () => {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(SYNC_ENABLED_KEY);
    localStorage.removeItem(LAST_SYNC_KEY);
    sessionStorage.removeItem(YANDEX_OAUTH_RETURN_KEY);
};

let activeYandexSyncs = 0;

const setSyncingState = (isStarting, setIsSyncing, onSyncingChange) => {
    activeYandexSyncs = Math.max(0, activeYandexSyncs + (isStarting ? 1 : -1));
    const syncing = activeYandexSyncs > 0;
    setIsSyncing(syncing);
    onSyncingChange?.(syncing);
};

const SyncSettings = ({vaultSession, onSync, onSyncingChange, variant = 'full'}) => {
    const [syncEnabled, setSyncEnabled] = useState(Boolean(localStorage.getItem(SYNC_ENABLED_KEY)));
    const [syncIntervalMs, setSyncIntervalMs] = useState(readSyncIntervalMs);
    const [lastSync, setLastSync] = useState(readLastSync);
    const [isSyncing, setIsSyncing] = useState(() => activeYandexSyncs > 0);
    const [token, setToken] = useState(() => readYandexToken());

    const intervalOptions = useMemo(() => {
        if (SYNC_INTERVAL_OPTIONS.some((option) => option.value === syncIntervalMs)) {
            return SYNC_INTERVAL_OPTIONS;
        }
        return [
            ...SYNC_INTERVAL_OPTIONS,
            {value: syncIntervalMs, label: formatIntervalLabel(syncIntervalMs)},
        ].sort((a, b) => a.value - b.value);
    }, [syncIntervalMs]);

    useEffect(() => {
        onSyncingChange?.(activeYandexSyncs > 0);
    }, [onSyncingChange]);

    const handleSyncIntervalChange = (value) => {
        const next = Number(value);
        if (!Number.isFinite(next) || next < MIN_SYNC_INTERVAL_MS) return;
        setSyncIntervalMs(next);
        persistSyncIntervalMs(next);
    };

    const handleYandexAuth = () => {
        sessionStorage.setItem(YANDEX_OAUTH_RETURN_KEY, 'settings');
        window.location.href = `https://oauth.yandex.ru/authorize?response_type=token&client_id=${YANDEX_APP_ID}&redirect_uri=${REDIRECT_URI}`;
    };

    const handleYandexDisconnect = () => {
        Modal.confirm({
            title: 'Отключить Яндекс.Диск?',
            content: 'Токен доступа будет удалён с этого устройства. Локальный сейф и файл на Диске останутся без изменений.',
            okText: 'Отключить',
            okType: 'danger',
            cancelText: 'Отмена',
            onOk: () => {
                clearYandexSyncStorage();
                setToken('');
                setSyncEnabled(false);
                setLastSync(null);
                message.success('Яндекс.Диск отключён');
            },
        });
    };

    useEffect(() => {
        if (syncEnabled) {
            localStorage.setItem(SYNC_ENABLED_KEY, "yes")
        } else {
            localStorage.removeItem(SYNC_ENABLED_KEY)
        }
    }, [syncEnabled]);

    useEffect(() => {
        const hash = window.location.hash.substr(1);
        const params = new URLSearchParams(hash);
        const accessToken = params.get('access_token');

        if (accessToken) {
            sessionStorage.setItem(YANDEX_OAUTH_RETURN_KEY, 'settings');
            persistYandexToken(accessToken);
            setToken(accessToken);
            window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        } else if (localStorage.getItem(TOKEN_STORAGE_KEY) && !sessionStorage.getItem(TOKEN_STORAGE_KEY)) {
            const legacy = localStorage.getItem(TOKEN_STORAGE_KEY);
            persistYandexToken(legacy);
            setToken(legacy);
        }
    }, []);

    const cleanupOldDeletions = (data) => {
        const deletionThreshold = new Date();
        deletionThreshold.setDate(deletionThreshold.getDate() - 1);

        return data.filter(item =>
            !item.deletedAt ||
            new Date(item.deletedAt) > deletionThreshold
        );
    };

    const syncData = useCallback(async (force = false) => {
        // force=true: immediate run after enabling (state may still be false)
        if ((!force && !syncEnabled) || !token || !vaultSession) return;

        setSyncingState(true, setIsSyncing, onSyncingChange);
        try {
            const salt = localStorage.getItem('salt');
            if (!salt) {
                throw new Error('Соль не задана');
            }

            const identity = await ensureIdentityForSalt(salt);
            const vault = getActiveVault(identity);
            if (!identity?.account?.id || !vault?.id) {
                throw new Error('Аккаунт или сейф не инициализированы');
            }

            const api = new YandexDiskApi(token);
            const remote = await resolveRemoteVault(api, {
                accountId: identity.account.id,
                vaultId: vault.id,
                salt,
            });
            const remotePath = remote.path;
            const remoteEncrypted = remote.content || null;
            const localEncrypted = localStorage.getItem('encryptedPasswords');

            const decryptedLocal = localEncrypted
                ? await vaultSession.decrypt(localEncrypted)
                : null;
            const decryptedRemote = remoteEncrypted
                ? await vaultSession.decrypt(remoteEncrypted)
                : null;

            if (localEncrypted && decryptedLocal === null) {
                throw new Error('Не удалось расшифровать локальный сейф');
            }
            if (remoteEncrypted && decryptedRemote === null) {
                throw new Error(
                    'Не удалось расшифровать сейф на Яндекс.Диске. Проверьте ключ и мастер-пароль.'
                );
            }

            const merged = mergePasswords(decryptedLocal || [], decryptedRemote || []);
            const cleanedData = cleanupOldDeletions(merged);
            const newEncrypted = await vaultSession.encrypt(cleanedData);

            if (!newEncrypted) {
                throw new Error('Не удалось зашифровать данные перед синхронизацией');
            }

            const localChanged = localEncrypted !== newEncrypted;
            const remoteChanged = remoteEncrypted !== newEncrypted || remote.fromLegacy;

            if (localChanged) localStorage.setItem('encryptedPasswords', newEncrypted);
            if (remoteChanged) await api.uploadFile(remotePath, newEncrypted);

            const uiData = cleanedData.filter(item => !item.deletedAt);
            onSync(uiData);

            const syncedAt = new Date();
            persistLastSync(syncedAt);
            setLastSync(syncedAt);

            if (force || localChanged || remoteChanged) {
                console.log(
                    `Синхронизация завершена. Обновлено ${localChanged ? 'локально' : ''}${localChanged && remoteChanged ? ' и ' : ''}${remoteChanged ? 'на диске' : ''}`
                );
            }
        } catch (error) {
            console.error('Sync error:', error);
            message.error(`Ошибка синхронизации: ${error.message}`);
        } finally {
            setSyncingState(false, setIsSyncing, onSyncingChange);
        }
    }, [syncEnabled, token, vaultSession, onSync, onSyncingChange]);

    useEffect(() => {
        if (!syncEnabled) return;

        const interval = setInterval(syncData, syncIntervalMs);
        return () => clearInterval(interval);
    }, [syncEnabled, syncData, syncIntervalMs]);

    if (variant === 'status') {
        if (!lastSync && !isSyncing) {
            return null;
        }

        return (
            <div className="sync-settings sync-settings--status">
                {lastSync && (
                    <span>Последняя синхронизация: {formatLastSync(lastSync)}</span>
                )}
                {isSyncing && <Spin size="small" style={{marginLeft: lastSync ? 8 : 0}}/>}
            </div>
        );
    }

    return (
        <div className="sync-settings">
            {!token ? (
                <Button onClick={handleYandexAuth} block>
                    Подключить Яндекс.Диск
                </Button>
            ) : (
                <>
                    <div className="sync-settings__toggle">
                        <Switch
                            checked={syncEnabled}
                            disabled={isSyncing}
                            onChange={checked => {
                                setSyncEnabled(checked);
                                if (checked) {
                                    syncData(true);
                                }
                            }}
                        />
                        <span>
                            Синхронизация с Яндекс.Диском {syncEnabled ? 'включена' : 'выключена'}
                        </span>
                        {isSyncing && <Spin size="small"/>}
                    </div>

                    <div className="sync-settings__interval">
                        <span className="sync-settings__interval-label">
                            Интервал синхронизации
                        </span>
                        <Select
                            value={syncIntervalMs}
                            options={intervalOptions}
                            onChange={handleSyncIntervalChange}
                            disabled={isSyncing}
                            style={{minWidth: 140}}
                        />
                    </div>

                    <div className="sync-settings__meta">
                        Последняя синхронизация: {formatLastSync(lastSync)}
                    </div>

                    <Button
                        danger
                        onClick={handleYandexDisconnect}
                        block
                        style={{marginTop: 12}}
                        disabled={isSyncing}
                    >
                        Отключить Яндекс.Диск
                    </Button>
                </>
            )}
        </div>
    );
};

export {
    SyncSettings,
}
